import * as Sentry from '@sentry/react';

/**
 * SENTRY ERROR REPORTING FUNCTIONS
 *
 * Use Cases:
 *
 * 1. sendSentryError(err, msgError)
 *    - Use when you want to send a simple error to Sentry with a custom message
 *    - Good for: Network errors, API errors, general application errors
 *    - Example: sendSentryError(err, 'Failed to fetch user data')
 *
 * 2. sendErrWithExtra(err, extra)
 *    - Use when you need to send additional context/data with the error
 *    - Good for: Complex errors that need debugging context, user data, file info, etc.
 *    - Example: sendErrWithExtra(err, { userId: 123, fileInfo: {...}, errorType: 'compression_failed' })
 *
 * Both functions automatically log to console (with AvoidSentryDups to prevent duplication)
 * and send to Sentry for monitoring.
 */

function errorFromProgressEvent(event) {
  const err = new Error('ProgressEvent captured as exception', {
    cause: event,
  });
  err.progressEvent = {
    lengthComputable: event.lengthComputable,
    loaded: event.loaded,
    total: event.total,
  };
  return err;
}

/**
 * Sends an error to Sentry with additional information.
 *
 * Use this function when you need to send complex debugging context with the error.
 * Perfect for errors that need additional data like user info, file details,
 * error types, or any other context that helps with debugging.
 *
 * @param {Error} err - The error object to be sent to Sentry.
 * @param {Object} extra - Additional information related to the error.
 *   It can be any object providing additional context about the error.
 *
 * @example
 * sendErrWithExtra(err, {
 *   userId: 123,
 *   fileInfo: { name: 'image.jpg', size: 1024 },
 *   errorType: 'compression_failed',
 *   bucket: 'user-uploads'
 * });
 */
export function sendErrWithExtra(err, extra) {
  // Log error details for debugging (AvoidSentryDups prevents duplication in Sentry)
  console.error('AvoidSentryDups: Error captured by Sentry', {
    error: err,
    extra,
    message: err.message || 'Unknown error',
  });

  Sentry.setExtra('extra', extra);
  Sentry.captureException(err);
}

/**
 * Sends a simple error to Sentry with a custom message.
 *
 * Use this function for basic error reporting when you just need to send
 * the error with a descriptive message. Good for network errors, API errors,
 * and general application errors that don't need complex debugging context.
 *
 * Automatically filters out 4xx HTTP errors (client errors) as they're usually
 * not critical for monitoring.
 *
 * @param {Error} err - The error object to be sent to Sentry.
 * @param {string} msgError - Custom message describing the error context.
 *
 * @example
 * sendSentryError(err, 'Failed to fetch user data from API');
 * sendSentryError(err, 'Error processing payment transaction');
 */
export default function sendSentryError(err, msgError) {
  if (!err) return;
  if (err.response && err.response.status >= 400 && err.response.status < 500) {
    return;
  }
  let sentryErr;
  if (err instanceof ProgressEvent) {
    sentryErr = errorFromProgressEvent(err);
  } else {
    sentryErr = err;
  }
  console.error('AvoidSentryDups: Sentry.captureException()', {
    customMsgError: msgError,
    sentryErr,
  });
  Sentry.captureException(sentryErr, {
    extra: {
      customMsgError: msgError,
    },
  });
}
